import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '~/services/supabase';
import type { User } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Check if user is in admin_users table
const checkAdminStatus = async (userId: string): Promise<boolean> => {
  console.log('[useAuth] checkAdminStatus called', { userId });
  try {
    const { data, error } = await supabase.from('admin_users').select('id').eq('auth_user_id', userId).maybeSingle();

    console.log('[useAuth] checkAdminStatus result', { data, error });

    if (error) {
      console.error('[useAuth] Error checking admin status:', error);
      return false;
    }

    if (!data) {
      console.log('[useAuth] checkAdminStatus: user NOT in admin_users table');
      return false;
    }

    // Update last login (fire and forget)
    supabase
      .from('admin_users')
      .update({ last_login_at: new Date().toISOString() })
      .eq('auth_user_id', userId)
      .then(
        () => {},
        () => {},
      );

    console.log('[useAuth] checkAdminStatus: user IS admin');
    return true;
  } catch (err) {
    console.error('[useAuth] Error checking admin status:', err);
    return false;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Raw session user from onAuthStateChange — triggers admin check in a separate effect.
  // We can't make authenticated Supabase DB queries inside onAuthStateChange because
  // it holds an internal auth lock; any query that needs getSession() to attach the
  // auth token will deadlock waiting for that same lock.
  const [pendingUser, setPendingUser] = useState<User | null | undefined>(undefined);

  // Step 1: Listen for auth events and capture the session user.
  // No async DB work here — just set state and get out of the lock.
  useEffect(() => {
    let mounted = true;
    console.log('[useAuth] useEffect(listener): subscribing to onAuthStateChange');

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[useAuth] onAuthStateChange:', event, {
        hasSession: !!session,
        hasUser: !!session?.user,
        userId: session?.user?.id,
        email: session?.user?.email,
        expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
      });
      if (!mounted) {
        console.log('[useAuth] onAuthStateChange: unmounted, skipping');
        return;
      }

      if (event === 'SIGNED_OUT') {
        console.log('[useAuth] onAuthStateChange: signed out, clearing state');
        setUser(null);
        setIsAdmin(false);
        setPendingUser(null);
        setLoading(false);
        return;
      }

      // For INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED — capture user for admin check
      console.log('[useAuth] onAuthStateChange: setting pendingUser');
      setPendingUser(session?.user ?? null);
    });

    return () => {
      console.log('[useAuth] useEffect(listener) cleanup: unmounting');
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Step 2: When pendingUser changes, run the admin check outside the auth lock.
  useEffect(() => {
    // undefined = initial state, haven't heard from onAuthStateChange yet
    if (pendingUser === undefined) return;

    // null = no session / signed out
    if (pendingUser === null) {
      console.log('[useAuth] useEffect(adminCheck): no pending user, clearing state');
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    console.log('[useAuth] useEffect(adminCheck): checking admin status for', pendingUser.email);

    const check = async () => {
      try {
        const adminStatus = await checkAdminStatus(pendingUser.id);
        if (cancelled) return;

        if (adminStatus) {
          console.log('[useAuth] useEffect(adminCheck): user is admin');
          setUser(pendingUser);
          setIsAdmin(true);
          setError(null);
        } else {
          console.log('[useAuth] useEffect(adminCheck): user is NOT admin, signing out');
          await supabase.auth.signOut().catch(() => {});
          setUser(null);
          setIsAdmin(false);
          setError('You do not have admin access');
        }
      } catch (err) {
        console.error('[useAuth] useEffect(adminCheck): EXCEPTION', err);
        if (!cancelled) {
          setUser(null);
          setIsAdmin(false);
        }
      } finally {
        if (!cancelled) {
          console.log('[useAuth] useEffect(adminCheck): setting loading=false');
          setLoading(false);
        }
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [pendingUser]);

  // Safety timeout — if nothing resolves in 8s, stop loading.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading((current) => {
        if (current) {
          console.warn('[useAuth] Auth initialization timed out (8s) — forcing loading=false');
          return false;
        }
        return current;
      });
    }, 8000);
    return () => clearTimeout(timeout);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth/callback',
        },
      });

      if (signInError) {
        throw signInError;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in with Google';
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setIsAdmin(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign out';
      setError(errorMessage);
    }
  }, []);

  return (
    <AuthContext value={{ user, loading, isAdmin, signIn, signInWithGoogle, signOut, error }}>{children}</AuthContext>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
